import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Trash2, Plus, ShieldCheck, ShieldOff, TrendingDown, TrendingUp } from 'lucide-react';
import { detectarDocumento, formatarDocumento } from '@profitlens/dominio';
import { formatarReais } from '@profitlens/calculo';
import { Cabecalho } from '../componentes/Cabecalho';
import { Carregando, Erro } from '../componentes/Estados';
import { Campo, CampoInteiro, CampoQuantidade, CampoReais, CampoTermos, Texto } from '../componentes/Campos';
import { Reais, Dias } from '../componentes/Valor';
import { Procedencia } from '../componentes/Procedencia';
import { useAuth } from '../contextos/AuthContext';
import { useUsuarios } from '../servicos/usuarios';
import { useProdutos } from '../servicos/produtos';
import {
  fornecedorVazio,
  idVinculo,
  registrarConsentimento,
  removerVinculo,
  revogarConsentimento,
  salvarFornecedor,
  salvarVinculo,
  useFornecedores,
  usePrecoHist,
  useVinculos,
  type RascunhoFornecedor,
} from '../servicos/fornecedores';
import type { CanalComunicacao } from '../tipos';

const ABAS = ['Identificação', 'Contato', 'Comercial', 'Produtos', 'Consentimento'] as const;
type Aba = (typeof ABAS)[number];
const CANAIS: CanalComunicacao[] = ['EMAIL', 'WHATSAPP', 'TELEFONE', 'PRESENCIAL', 'OUTRO'];

export function FornecedorFicha() {
  const { id } = useParams<{ id: string }>();
  const novo = id === 'novo';
  const fornecedorId = novo ? null : (id ?? null);

  const navegar = useNavigate();
  const { perfil, podeEscrever } = useAuth();
  const { fornecedores, carregando } = useFornecedores();
  const { usuarios } = useUsuarios();

  const [aba, setAba] = useState<Aba>('Identificação');
  const [rascunho, setRascunho] = useState<RascunhoFornecedor>(fornecedorVazio());
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const existente = fornecedores.find((f) => f.id === fornecedorId);

  useEffect(() => {
    if (!existente) return;
    const { id: _i, criadoEm, criadoPor, atualizadoEm, atualizadoPor, ...resto } = existente;
    setRascunho(resto);
  }, [existente?.id]);

  const nomes = useMemo(() => Object.fromEntries(usuarios.map((u) => [u.id, u.nome])), [usuarios]);
  const doc = rascunho.documento ? detectarDocumento(rascunho.documento) : null;

  if (!novo && carregando) return <Carregando />;
  if (!novo && !existente && !carregando) {
    return <Erro titulo="Fornecedor não encontrado" detalhe="Ele pode ter sido removido." />;
  }

  const editar = <K extends keyof RascunhoFornecedor>(campo: K, valor: RascunhoFornecedor[K]) =>
    setRascunho((r) => ({ ...r, [campo]: valor }));

  async function salvar(e: FormEvent) {
    e.preventDefault();
    if (!perfil) return;
    setErro(null);
    setSalvando(true);
    try {
      const novoId = await salvarFornecedor(fornecedorId, rascunho, perfil.id);
      if (novo) navegar(`/fornecedores/${novoId}`, { replace: true });
    } catch (ex) {
      setErro((ex as Error).message);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <form onSubmit={salvar} className="flex flex-col gap-5">
      <Cabecalho
        titulo={novo ? 'Novo fornecedor' : rascunho.nomeFantasia || rascunho.razaoSocial || 'Fornecedor'}
        acoes={
          <div className="flex gap-2">
            <Link
              to="/fornecedores"
              className="inline-flex items-center gap-1.5 rounded border border-regua-2 px-3 py-1.5 text-sm text-tinta-2 hover:bg-superficie-2"
            >
              <ArrowLeft className="size-4" aria-hidden />
              Voltar
            </Link>
            {podeEscrever && (
              <button
                type="submit"
                disabled={salvando || rascunho.razaoSocial.trim() === ''}
                className="rounded bg-marca px-4 py-1.5 text-sm font-semibold text-marca-tinta hover:opacity-90 disabled:opacity-60"
              >
                {salvando ? 'Salvando…' : 'Salvar'}
              </button>
            )}
          </div>
        }
      />

      {erro && <Erro titulo="Não foi possível salvar" detalhe={erro} />}

      <div className="flex flex-wrap gap-1 border-b border-regua">
        {ABAS.map((a) => (
          <button
            key={a}
            type="button"
            onClick={() => setAba(a)}
            aria-current={aba === a}
            className={`-mb-px border-b-2 px-3 py-1.5 text-sm ${
              aba === a
                ? 'border-marca font-semibold text-marca'
                : 'border-transparent text-tinta-2 hover:text-tinta'
            }`}
          >
            {a}
            {a === 'Consentimento' && !rascunho.consentimento?.ativo && (
              <span className="ml-1.5 text-c-res" title="Sem consentimento ativo">•</span>
            )}
          </button>
        ))}
      </div>

      {aba === 'Identificação' && (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Texto rotulo="Razão social ou nome *" valor={rascunho.razaoSocial} aoMudar={(v) => editar('razaoSocial', v ?? '')} obrigatorio desabilitado={!podeEscrever} />
          </div>
          <Texto rotulo="Nome fantasia" valor={rascunho.nomeFantasia} aoMudar={(v) => editar('nomeFantasia', v)} desabilitado={!podeEscrever} />
          <Campo
            rotulo="CPF ou CNPJ"
            erro={doc && !doc.valido ? doc.motivo : null}
            dica={
              doc?.valido
                ? `${doc.tipo} válido — ${formatarDocumento(doc.valor)}`
                : 'O dígito verificador é conferido aqui. CNPJ com letras (desde 31/07/2026) também é aceito.'
            }
          >
            <input
              type="text"
              disabled={!podeEscrever}
              value={rascunho.documento ?? ''}
              onChange={(e) => editar('documento', e.target.value === '' ? null : e.target.value)}
              placeholder="00.000.000/0000-00"
              className="w-full rounded border border-regua-2 bg-papel px-3 py-1.5 font-mono text-sm outline-none focus:border-marca disabled:opacity-60"
            />
          </Campo>
          <Texto rotulo="Site" valor={rascunho.site} aoMudar={(v) => editar('site', v)} desabilitado={!podeEscrever} />
          <Campo rotulo="Avaliação interna">
            <select
              value={rascunho.avaliacaoInterna ?? ''}
              disabled={!podeEscrever}
              onChange={(e) => editar('avaliacaoInterna', e.target.value === '' ? null : Number(e.target.value))}
              className="w-full rounded border border-regua-2 bg-papel px-3 py-1.5 text-sm outline-none focus:border-marca disabled:opacity-60"
            >
              <option value="">Sem avaliação</option>
              {[1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={n}>{'★'.repeat(n)}{'☆'.repeat(5 - n)}</option>
              ))}
            </select>
          </Campo>
          <div className="sm:col-span-2">
            <Texto rotulo="Observações" valor={rascunho.observacoes} aoMudar={(v) => editar('observacoes', v)} desabilitado={!podeEscrever} />
          </div>
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input type="checkbox" checked={rascunho.ativo} disabled={!podeEscrever} onChange={(e) => editar('ativo', e.target.checked)} className="size-4 accent-[var(--color-marca)]" />
            Ativo
          </label>
        </div>
      )}

      {aba === 'Contato' && (
        <div className="grid gap-4 sm:grid-cols-2">
          <Texto rotulo="Nome do contato" valor={rascunho.contatoNome} aoMudar={(v) => editar('contatoNome', v)} desabilitado={!podeEscrever} />
          <Texto rotulo="E-mail" valor={rascunho.email} aoMudar={(v) => editar('email', v)} desabilitado={!podeEscrever} />
          <Texto rotulo="Telefone" valor={rascunho.telefone} aoMudar={(v) => editar('telefone', v)} desabilitado={!podeEscrever} />
          <Texto rotulo="WhatsApp" valor={rascunho.whatsapp} aoMudar={(v) => editar('whatsapp', v)} desabilitado={!podeEscrever} />
          <Campo rotulo="Canal preferencial">
            <select
              value={rascunho.canalPreferencial ?? ''}
              disabled={!podeEscrever}
              onChange={(e) => editar('canalPreferencial', (e.target.value || null) as CanalComunicacao | null)}
              className="w-full rounded border border-regua-2 bg-papel px-3 py-1.5 text-sm outline-none focus:border-marca disabled:opacity-60"
            >
              <option value="">Não definido</option>
              {CANAIS.map((c) => (<option key={c} value={c}>{c}</option>))}
            </select>
          </Campo>
          <Texto rotulo="Horário adequado para contato" valor={rascunho.horarioContato} aoMudar={(v) => editar('horarioContato', v)} desabilitado={!podeEscrever} placeholder="ex.: 9h às 17h, dias úteis" />
        </div>
      )}

      {aba === 'Comercial' && (
        <div className="grid gap-4 sm:grid-cols-2">
          <CampoInteiro rotulo="Prazo médio de entrega" valor={rascunho.prazoMedioEntregaDias} aoMudar={(v) => editar('prazoMedioEntregaDias', v)} sufixo="dias" desabilitado={!podeEscrever} />
          <CampoReais rotulo="Pedido mínimo" cents={rascunho.pedidoMinimoCents} aoMudar={(v) => editar('pedidoMinimoCents', v)} desabilitado={!podeEscrever} />
          <Texto rotulo="Condições de pagamento" valor={rascunho.condicoesPagamento} aoMudar={(v) => editar('condicoesPagamento', v)} desabilitado={!podeEscrever} placeholder="ex.: 30/60 dias" />
          <Texto rotulo="Política de frete" valor={rascunho.politicaFrete} aoMudar={(v) => editar('politicaFrete', v)} desabilitado={!podeEscrever} placeholder="ex.: CIF acima de R$ 2.000" />
          <div className="sm:col-span-2">
            <CampoTermos rotulo="Regiões atendidas" termos={rascunho.regioesAtendidas} aoMudar={(v) => editar('regioesAtendidas', v)} desabilitado={!podeEscrever} dica="Estados ou regiões, um por vez." />
          </div>
        </div>
      )}

      {aba === 'Produtos' && <AbaProdutos fornecedorId={fornecedorId} podeEscrever={podeEscrever} usuarioId={perfil?.id ?? ''} />}

      {aba === 'Consentimento' && (
        <AbaConsentimento
          fornecedorId={fornecedorId}
          consentimento={rascunho.consentimento}
          podeEscrever={podeEscrever}
        />
      )}

      {existente && (
        <Procedencia
          criadoPor={existente.criadoPor}
          criadoEm={existente.criadoEm}
          atualizadoPor={existente.atualizadoPor}
          atualizadoEm={existente.atualizadoEm}
          entidade="fornecedores"
          entidadeId={existente.id}
          nomes={nomes}
        />
      )}
    </form>
  );
}

function AbaConsentimento({
  fornecedorId,
  consentimento,
  podeEscrever,
}: {
  fornecedorId: string | null;
  consentimento: RascunhoFornecedor['consentimento'];
  podeEscrever: boolean;
}) {
  const [canal, setCanal] = useState<CanalComunicacao>('EMAIL');
  const [origem, setOrigem] = useState('');

  if (!fornecedorId) {
    return (
      <p className="rounded-md border border-dashed border-regua-2 bg-superficie px-4 py-6 text-center text-sm text-tinta-2">
        Salve o fornecedor primeiro.
      </p>
    );
  }

  const ativo = consentimento?.ativo === true;

  return (
    <div className="flex flex-col gap-4">
      <div
        className={`flex flex-col gap-2 rounded-md border-l-3 px-4 py-3 ${
          ativo ? 'border-c-via bg-c-via/10' : 'border-c-res bg-c-res/10'
        }`}
      >
        <p className={`flex items-center gap-2 text-sm font-semibold ${ativo ? 'text-c-via' : 'text-c-res'}`}>
          {ativo ? <ShieldCheck className="size-4" aria-hidden /> : <ShieldOff className="size-4" aria-hidden />}
          {ativo ? `Comunicação autorizada por ${consentimento.canal}` : 'Sem consentimento ativo'}
        </p>
        <p className="text-sm text-tinta-2">
          {ativo
            ? 'O Hermes pode enviar pedidos de cotação por este canal.'
            : 'O Hermes não envia mensagem a este fornecedor. O registro manual de cotação continua permitido — consentimento rege comunicação, não anotação.'}
        </p>
        {consentimento?.origem && (
          <p className="text-xs text-tinta-3">Obtido assim: {consentimento.origem}</p>
        )}
      </div>

      {podeEscrever &&
        (ativo ? (
          <button
            type="button"
            onClick={() => void revogarConsentimento(fornecedorId)}
            className="self-start rounded border border-pend px-3 py-1.5 text-sm font-medium text-pend hover:bg-pend/10"
          >
            Revogar consentimento
          </button>
        ) : (
          <div className="flex flex-wrap items-end gap-2 rounded-md border border-regua-2 bg-superficie p-3">
            <Campo rotulo="Canal autorizado">
              <select
                value={canal}
                onChange={(e) => setCanal(e.target.value as CanalComunicacao)}
                className="rounded border border-regua-2 bg-papel px-3 py-1.5 text-sm outline-none focus:border-marca"
              >
                {CANAIS.map((c) => (<option key={c} value={c}>{c}</option>))}
              </select>
            </Campo>
            <div className="min-w-56 flex-1">
              <Campo rotulo="Como o consentimento foi obtido" dica="Registre a origem: é o que sustenta a autorização se for questionada.">
                <input
                  type="text"
                  value={origem}
                  onChange={(e) => setOrigem(e.target.value)}
                  placeholder="ex.: acordo verbal registrado em reunião de 15/01/2026"
                  className="w-full rounded border border-regua-2 bg-papel px-3 py-1.5 text-sm outline-none focus:border-marca"
                />
              </Campo>
            </div>
            <button
              type="button"
              disabled={origem.trim() === ''}
              onClick={() => void registrarConsentimento(fornecedorId, canal, origem.trim())}
              className="rounded bg-marca px-3 py-1.5 text-sm font-semibold text-marca-tinta hover:opacity-90 disabled:opacity-60"
            >
              Registrar
            </button>
          </div>
        ))}
    </div>
  );
}

function AbaProdutos({
  fornecedorId,
  podeEscrever,
  usuarioId,
}: {
  fornecedorId: string | null;
  podeEscrever: boolean;
  usuarioId: string;
}) {
  const { produtos } = useProdutos();
  const vinculos = useVinculos(fornecedorId ? { fornecedorId } : {});
  const [produtoId, setProdutoId] = useState('');
  const [aberto, setAberto] = useState<string | null>(null);

  if (!fornecedorId) {
    return (
      <p className="rounded-md border border-dashed border-regua-2 bg-superficie px-4 py-6 text-center text-sm text-tinta-2">
        Salve o fornecedor primeiro. Os vínculos precisam de um fornecedor existente.
      </p>
    );
  }

  const vinculados = new Set(vinculos.map((v) => v.produtoId));
  const disponiveis = produtos.filter((p) => p.ativo && !vinculados.has(p.id));

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-tinta-2">
        O portfólio deste fornecedor. É a partir daqui que o Hermes sabe o que pedir a quem.
      </p>

      {podeEscrever && disponiveis.length > 0 && (
        <div className="flex flex-wrap items-end gap-2 rounded-md border border-regua-2 bg-superficie p-3">
          <div className="min-w-56 flex-1">
            <Campo rotulo="Vincular produto">
              <select
                value={produtoId}
                onChange={(e) => setProdutoId(e.target.value)}
                className="w-full rounded border border-regua-2 bg-papel px-3 py-1.5 text-sm outline-none focus:border-marca"
              >
                <option value="">Escolha um produto</option>
                {disponiveis.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nome} {p.marca ? `· ${p.marca}` : ''}
                  </option>
                ))}
              </select>
            </Campo>
          </div>
          <button
            type="button"
            disabled={!produtoId}
            onClick={async () => {
              await salvarVinculo(fornecedorId, produtoId, {}, usuarioId);
              setProdutoId('');
            }}
            className="inline-flex items-center gap-1.5 rounded bg-marca px-3 py-1.5 text-sm font-semibold text-marca-tinta hover:opacity-90 disabled:opacity-60"
          >
            <Plus className="size-4" aria-hidden />
            Vincular
          </button>
        </div>
      )}

      {vinculos.length === 0 ? (
        <p className="rounded-md border border-dashed border-regua-2 bg-superficie px-4 py-6 text-center text-sm text-tinta-2">
          Nenhum produto vinculado. Sem vínculo, este fornecedor não entra em nenhuma cotação.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {vinculos.map((v) => {
            const p = produtos.find((x) => x.id === v.produtoId);
            const expandido = aberto === v.produtoId;
            return (
              <li key={v.id} className="rounded-md border border-regua-2 bg-superficie">
                <div className="flex flex-wrap items-center gap-3 px-3 py-2">
                  <button
                    type="button"
                    onClick={() => setAberto(expandido ? null : v.produtoId)}
                    className="flex-1 text-left text-sm font-medium hover:text-marca"
                  >
                    {p?.nome ?? 'produto removido'}
                  </button>
                  <span className="text-sm">
                    <Reais cents={v.ultimoPrecoCents} />
                  </span>
                  <span className="text-xs text-tinta-3">
                    <Dias dias={v.prazoEntregaDias} />
                  </span>
                  {podeEscrever && (
                    <button
                      type="button"
                      onClick={() => void removerVinculo(fornecedorId, v.produtoId)}
                      aria-label="Remover vínculo"
                      className="text-tinta-3 hover:text-pend"
                    >
                      <Trash2 className="size-4" />
                    </button>
                  )}
                </div>
                {expandido && (
                  <DetalheVinculo
                    fornecedorId={fornecedorId}
                    produtoId={v.produtoId}
                    vinculo={v}
                    podeEscrever={podeEscrever}
                    usuarioId={usuarioId}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function DetalheVinculo({
  fornecedorId,
  produtoId,
  vinculo,
  podeEscrever,
  usuarioId,
}: {
  fornecedorId: string;
  produtoId: string;
  vinculo: { codigoFornecedor: string | null; ultimoPrecoCents: number | null; quantidadeMinimaMilli: number | null; prazoEntregaDias: number | null; condicaoPagamento: string | null };
  podeEscrever: boolean;
  usuarioId: string;
}) {
  const historico = usePrecoHist(idVinculo(fornecedorId, produtoId));
  const [dados, setDados] = useState(vinculo);
  const [salvando, setSalvando] = useState(false);

  return (
    <div className="flex flex-col gap-4 border-t border-regua px-3 py-3">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Texto rotulo="Código no fornecedor" valor={dados.codigoFornecedor} aoMudar={(v) => setDados((d) => ({ ...d, codigoFornecedor: v }))} desabilitado={!podeEscrever} />
        <CampoReais rotulo="Último preço" cents={dados.ultimoPrecoCents} aoMudar={(v) => setDados((d) => ({ ...d, ultimoPrecoCents: v }))} desabilitado={!podeEscrever} />
        <CampoQuantidade rotulo="Quantidade mínima" milli={dados.quantidadeMinimaMilli} aoMudar={(v) => setDados((d) => ({ ...d, quantidadeMinimaMilli: v }))} desabilitado={!podeEscrever} />
        <CampoInteiro rotulo="Prazo de entrega" valor={dados.prazoEntregaDias} aoMudar={(v) => setDados((d) => ({ ...d, prazoEntregaDias: v }))} sufixo="dias" desabilitado={!podeEscrever} />
        <div className="sm:col-span-2">
          <Texto rotulo="Condição de pagamento" valor={dados.condicaoPagamento} aoMudar={(v) => setDados((d) => ({ ...d, condicaoPagamento: v }))} desabilitado={!podeEscrever} />
        </div>
      </div>

      {podeEscrever && (
        <button
          type="button"
          disabled={salvando}
          onClick={async () => {
            setSalvando(true);
            await salvarVinculo(fornecedorId, produtoId, dados, usuarioId);
            setSalvando(false);
          }}
          className="self-start rounded border border-marca px-3 py-1.5 text-sm font-medium text-marca hover:bg-marca/10 disabled:opacity-60"
        >
          {salvando ? 'Salvando…' : 'Salvar vínculo'}
        </button>
      )}

      <div className="flex flex-col gap-1.5">
        <h4 className="font-mono text-[0.62rem] tracking-wider text-tinta-3 uppercase">
          Histórico de preços
        </h4>
        {historico.length === 0 ? (
          <p className="text-xs text-tinta-3">
            Nenhuma alteração registrada ainda. Toda mudança de preço entra aqui e não pode ser reescrita.
          </p>
        ) : (
          <ul className="flex flex-col gap-1 text-xs">
            {historico.map((h) => {
              const subiu = h.precoAnteriorCents !== null && h.precoCents > h.precoAnteriorCents;
              const desceu = h.precoAnteriorCents !== null && h.precoCents < h.precoAnteriorCents;
              return (
                <li key={h.id} className="flex items-center gap-2">
                  <span className="num text-tinta-3">
                    {h.vigenteEm.split('-').reverse().join('/')}
                  </span>
                  <span className="num font-medium">{formatarReais(h.precoCents)}</span>
                  {h.precoAnteriorCents !== null && (
                    <span className={`inline-flex items-center gap-0.5 ${subiu ? 'text-c-mar' : desceu ? 'text-c-via' : 'text-tinta-3'}`}>
                      {subiu ? <TrendingUp className="size-3" aria-hidden /> : desceu ? <TrendingDown className="size-3" aria-hidden /> : null}
                      <span className="num">antes {formatarReais(h.precoAnteriorCents)}</span>
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
